import React, { useState } from "react";
import { Modal, ModalBody } from "reactstrap";
import styled from "styled-components";
import { FaRobot, FaTimes, FaMapMarkerAlt, FaTag, FaDollarSign, FaLayerGroup } from "react-icons/fa";
import { BsStars } from "react-icons/bs";
import { apiParseSearchQuery } from "../../api/ai.api";
import { colors } from "../../utils/Colors";

// ── Example queries (Jobs / Gigs / Projects) ──────────────────────────────────
const EXAMPLES = [
  { icon: "🔧", text: "I need a plumber in New York under $75",    hint: "Job"     },
  { icon: "💻", text: "React developer for ecommerce project",       hint: "Project" },
  { icon: "✍️", text: "Content writer under $50 in Texas",          hint: "Gig"     },
];

const TYPE_COLORS  = { gig: "#0fd3bb", job: "#e67e22", project: "#446EE7" };
const TYPE_LABELS  = { gig: "Service Provider", job: "Job", project: "Project" };

// ── Helpers ───────────────────────────────────────────────────────────────────
const getCurrentCoords = () =>
  new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => resolve({ lat: coords.latitude, lng: coords.longitude }),
      () => resolve(null),
      { timeout: 4000 }
    );
  });

// ── Component ─────────────────────────────────────────────────────────────────
const AISearchModal = ({ isOpen, toggle, onFiltersReady }) => {
  const [query,   setQuery]   = useState("");
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [result,  setResult]  = useState(null);

  const handleSearch = async (q = query) => {
    const trimmed = (q || "").trim();
    if (trimmed.length < 3) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      // Inject real coords when user says "near me"
      let finalQuery = trimmed;
      const isNearMe = /near me|nearby|close to me/i.test(trimmed);
      if (isNearMe) {
        const coords = await getCurrentCoords();
        if (coords) {
          finalQuery = trimmed.replace(
            /near me|nearby|close to me/gi,
            `near ${coords.lat.toFixed(5)},${coords.lng.toFixed(5)}`
          );
        }
      }

      const res    = await apiParseSearchQuery(finalQuery);
      const parsed = res.data?.data ?? res.data ?? {};
      // Fallback: inject current coords if "near me" but API didn't geocode
      if (isNearMe && !parsed.location?.latitude) {
        const coords = await getCurrentCoords();
        if (coords) {
          parsed.location = parsed.location || {};
          parsed.location.latitude  = coords.lat;
          parsed.location.longitude = coords.lng;
          parsed.location.nearMe    = true;
        }
      }

      // ── Auto-apply immediately on success ──────────────────────────────
      if (onFiltersReady) onFiltersReady(parsed);
      handleClose();
    } catch {
      setError("AI search failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    if (onFiltersReady && result) onFiltersReady(result);
    handleClose();
  };

  const handleClose = () => {
    toggle();
    setTimeout(() => { setQuery(""); setError(null); setLoading(false); setResult(null); }, 300);
  };

  const hasLocation = result?.location?.city || result?.location?.state || result?.location?.nearMe;
  const hasCoords   = !!(result?.location?.latitude && result?.location?.longitude);
  const primaryType = result?.types?.[0];

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg" centered aria-label="AI search">
      <ModalBodyStyled colors={colors}>

        {/* ── Header ── */}
        <div className="ai-header">
          <div className="ai-title">
            <FaRobot size={19} />
            <span>AI Smart Search</span>
          </div>
          <button className="btn-close-modal" onClick={handleClose} aria-label="Close">
            <FaTimes size={15} />
          </button>
        </div>

        {/* ── Input area ── */}
        <div className="ai-input-area">
          <p className="input-hint">
            Type what you need in plain English — AI extracts filters automatically.
          </p>
          <textarea
            className="ai-textarea"
            placeholder={`e.g. "I need a plumber in New York under $75"\n     "React developer for startup project"\n     "Electrician near me under $70"`}
            value={query}
            onChange={(e) => { setQuery(e.target.value); if (result) setResult(null); }}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSearch(); } }}
            rows={3}
            maxLength={300}
            autoFocus
          />
          <div className="input-row">
            <span className="char-count">{query.length}/300</span>
            <button
              className="btn-search"
              onClick={() => handleSearch()}
              disabled={loading || query.trim().length < 3}
            >
              {loading
                ? <span className="spinner" />
                : <><BsStars size={14} /> Search with AI</>
              }
            </button>
          </div>
        </div>

        {/* ── Example pills (shown when idle) ── */}
        {!loading && !result && (
          <div className="examples-section">
            <p className="examples-label">Examples:</p>
            <div className="examples-grid">
              {EXAMPLES.map((ex) => (
                <button
                  className="example-pill"
                >
                  <span className="ex-icon">{ex.icon}</span>
                  <span className="ex-text">{ex.text}</span>
                  <span className="ex-hint">{ex.hint}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Loading ── */}
        {loading && (
          <div className="ai-loading">
            <div className="pulse"><FaRobot size={32} /></div>
            <p>Understanding your search…</p>
            <p className="sub">Extracting filters &amp; geocoding location</p>
          </div>
        )}

        {/* ── Error ── */}
        {error && !loading && <div className="ai-error">{error}</div>}

        {/* ── Result preview card ── */}
        {result && !loading && (
          <div className="result-card">
            <div className="result-card-header">
              <BsStars size={15} />
              <span>AI understood your search</span>
              {primaryType && (
                <span className="type-chip" style={{ background: TYPE_COLORS[primaryType] || "#0fd3bb" }}>
                  {TYPE_LABELS[primaryType] || primaryType}
                </span>
              )}
            </div>

            {result.summary && <p className="result-summary">"{result.summary}"</p>}

            <div className="result-rows">
              {/* Searching in (types) */}
              {result.types?.length > 0 && (
                <div className="result-row">
                  <FaLayerGroup className="r-icon" />
                  <span className="r-label">Searching in:</span>
                  <div className="type-badges">
                    {result.types.map((t) => (
                      <span key={t} className="type-badge"
                        style={{ background: TYPE_COLORS[t] || "#0fd3bb" }}>
                        {TYPE_LABELS[t] || t}s
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Keyword */}
              {(result.subCategory || result.searchText) && (
                <div className="result-row">
                  <FaTag className="r-icon" />
                  <span className="r-label">Keyword:</span>
                  <strong>{result.subCategory || result.searchText}</strong>
                  {result.skills?.length > 0 && (
                    <div className="skill-pills">
                      {result.skills.map((s) => <span key={s} className="skill-pill">{s}</span>)}
                    </div>
                  )}
                </div>
              )}

              {/* Location */}
              {hasLocation && (
                <div className="result-row">
                  <FaMapMarkerAlt className="r-icon" />
                  <span className="r-label">Location:</span>
                  <span>
                    {result.location.nearMe
                      ? "Near your current location"
                      : [result.location.city, result.location.state, result.location.country]
                          .filter(Boolean).join(", ")
                    }
                    {hasCoords && <span className="geo-badge">📍 Geocoded</span>}
                  </span>
                </div>
              )}

              {/* Budget */}
              {(result.budgetMin || result.budgetMax) && (
                <div className="result-row">
                  <FaDollarSign className="r-icon" />
                  <span className="r-label">Budget:</span>
                  <span>
                    {result.budgetMin && result.budgetMax
                      ? `$${result.budgetMin} – $${result.budgetMax}`
                      : result.budgetMax ? `Up to $${result.budgetMax}`
                      : `From $${result.budgetMin}`}
                  </span>
                </div>
              )}
            </div>

            <div className="result-actions">
              <button className="btn-apply" onClick={handleApply}>
                <BsStars size={14} />
                {hasCoords ? "Show on Map" : "Show Results"}
              </button>
              <button className="btn-refine" onClick={() => setResult(null)}>
                ← Refine
              </button>
            </div>
          </div>
        )}

      </ModalBodyStyled>
    </Modal>
  );
};

// ── Styled ────────────────────────────────────────────────────────────────────
const ModalBodyStyled = styled(ModalBody)`
  padding: 0;
  border-radius: 12px;
  overflow: hidden;

  /* Header */
  .ai-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.9rem 1.2rem;
    background: linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%);
    color: #fff;

    .ai-title {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 1rem;
      font-weight: 700;
    }

    .badge-powered {
      background: rgba(15,211,187,0.2);
      border: 1px solid rgba(15,211,187,0.45);
      color: #0fd3bb;
      border-radius: 20px;
      padding: 1px 8px;
      font-size: 0.68rem;
      font-weight: 500;
    }

    .btn-close-modal {
      background: rgba(255,255,255,0.12);
      border: none;
      color: #fff;
      border-radius: 50%;
      width: 28px; height: 28px;
      display: flex; align-items: center; justify-content: center;
      cursor: pointer;
      transition: background 0.2s;
      &:hover { background: rgba(255,255,255,0.25); }
    }
  }

  /* Input */
  .ai-input-area {
    padding: 0.9rem 1.2rem 0.6rem;
    background: #f8f9fc;
    border-bottom: 1px solid #eee;

    .input-hint {
      font-size: 0.78rem;
      color: #999;
      margin: 0 0 0.5rem;
    }

    .ai-textarea {
      width: 100%;
      background: #fff;
      border: 1.5px solid #e0e0e0;
      border-radius: 10px;
      padding: 0.7rem 1rem;
      font-size: 0.92rem;
      resize: none;
      outline: none;
      line-height: 1.6;
      transition: border-color 0.2s;
      &:focus { border-color: ${({ colors }) => colors.color.secondary}; }
    }

    .input-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 0.5rem;
    }

    .char-count { font-size: 0.7rem; color: #ccc; }

    .btn-search {
      display: flex;
      align-items: center;
      gap: 6px;
      background: ${({ colors }) => colors.color.secondary};
      color: #fff;
      border: none;
      border-radius: 8px;
      padding: 0.45rem 1.1rem;
      font-size: 0.86rem;
      font-weight: 600;
      cursor: pointer;
      transition: opacity 0.2s;
      &:disabled { opacity: 0.4; cursor: not-allowed; }
      &:not(:disabled):hover { opacity: 0.85; }
    }
  }

  /* Examples */
  .examples-section {
    padding: 0.9rem 1.2rem 1rem;

    .examples-label {
      font-size: 0.72rem;
      color: #bbb;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      margin: 0 0 0.6rem;
    }

    .examples-grid {
      display: flex;
      flex-direction: column;
      gap: 0.4rem;
    }

    .example-pill {
      display: flex;
      align-items: center;
      gap: 0.55rem;
      background: #fff;
      border: 1px solid #ececec;
      border-radius: 10px;
      padding: 0.45rem 0.9rem;
      cursor: pointer;
      text-align: left;
      transition: all 0.18s;

      &:hover {
        border-color: ${({ colors }) => colors.color.secondary};
        background: rgba(15,211,187,0.04);
      }

      .ex-icon { font-size: 0.95rem; flex-shrink: 0; }
      .ex-text { font-size: 0.83rem; color: #444; flex: 1; }
      .ex-hint {
        font-size: 0.68rem;
        color: ${({ colors }) => colors.color.secondary};
        background: rgba(15,211,187,0.1);
        border-radius: 10px;
        padding: 1px 7px;
        flex-shrink: 0;
      }
    }
  }

  /* Loading */
  .ai-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 2rem 1rem;
    gap: 0.45rem;
    color: #777;

    .pulse {
      color: ${({ colors }) => colors.color.secondary};
      animation: pulse 1.3s ease-in-out infinite;
    }
    p { margin: 0; font-size: 0.93rem; }
    .sub { font-size: 0.75rem; color: #bbb; }

    @keyframes pulse {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.45; transform: scale(1.12); }
    }
  }

  /* Error */
  .ai-error {
    margin: 0.75rem 1.2rem;
    padding: 0.65rem 1rem;
    background: #fdecea;
    border-left: 4px solid #e53935;
    border-radius: 6px;
    color: #c62828;
    font-size: 0.83rem;
  }

  /* Result card */
  .result-card {
    border-top: 1px solid #f0f0f0;

    .result-card-header {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.65rem 1.2rem;
      background: linear-gradient(135deg, #0fd3bb, #08b8a2);
      color: #0f1f3d;
      font-weight: 700;
      font-size: 0.86rem;

      .type-chip {
        color: #fff;
        border-radius: 10px;
        padding: 1px 9px;
        font-size: 0.72rem;
        font-weight: 600;
        margin-left: auto;
      }
    }

    .result-summary {
      padding: 0.6rem 1.2rem 0;
      color: #666;
      font-style: italic;
      font-size: 0.83rem;
      margin: 0;
    }

    .result-rows {
      padding: 0.55rem 1.2rem;
      display: flex;
      flex-direction: column;
      gap: 0.45rem;
    }

    .result-row {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 0.4rem;
      font-size: 0.85rem;
      color: #333;

      .r-icon { color: ${({ colors }) => colors.color.secondary}; font-size: 0.75rem; flex-shrink: 0; }
      .r-label { color: #aaa; flex-shrink: 0; font-size: 0.8rem; }

      .geo-badge {
        font-size: 0.68rem;
        background: rgba(15,211,187,0.12);
        color: #08a890;
        border-radius: 10px;
        padding: 1px 6px;
        margin-left: 3px;
      }

      .skill-pills { display: flex; gap: 4px; flex-wrap: wrap; }
      .skill-pill {
        background: #eff3ff;
        border: 1px solid #d0d8ff;
        border-radius: 8px;
        padding: 1px 6px;
        font-size: 0.7rem;
        color: #446EE7;
      }
    }

    .type-badges { display: flex; gap: 4px; flex-wrap: wrap; }
    .type-badge {
      color: #fff;
      border-radius: 8px;
      padding: 1px 8px;
      font-size: 0.72rem;
      font-weight: 600;
    }

    .result-actions {
      display: flex;
      gap: 0.6rem;
      padding: 0.7rem 1.2rem 0.9rem;
      border-top: 1px solid #f4f4f4;

      .btn-apply {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        background: ${({ colors }) => colors.color.secondary};
        color: #fff;
        border: none;
        border-radius: 8px;
        padding: 0.52rem 1rem;
        font-size: 0.86rem;
        font-weight: 600;
        cursor: pointer;
        transition: opacity 0.2s;
        &:hover { opacity: 0.85; }
      }

      .btn-refine {
        background: transparent;
        border: 1.5px solid #e0e0e0;
        border-radius: 8px;
        padding: 0.52rem 1rem;
        font-size: 0.83rem;
        color: #888;
        cursor: pointer;
        transition: all 0.18s;
        &:hover { border-color: ${({ colors }) => colors.color.secondary}; color: ${({ colors }) => colors.color.secondary}; }
      }
    }
  }

  /* Spinner */
  .spinner {
    width: 14px; height: 14px;
    border: 2px solid rgba(255,255,255,0.3);
    border-top-color: #fff;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
    display: inline-block;
  }
  @keyframes spin { to { transform: rotate(360deg); } }
`;

export default AISearchModal;

